import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import { LessonStatus, InformationText, PrimaryButton, SecondaryButton, ManageButton, AcceptButton, LessonBadge } from './shared/CommonElements';
import { getBackNavigationPath, getBackButtonText, isUserAdmin } from '../utils/adminUtils';
import RescheduleLessonPopup from './RescheduleLessonPopup';
import ConfirmationPopup from './ConfirmationPopup';
import { isLessonWithinRestrictionHours, LESSON_CONFIG } from '../config/lessonConfig';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  white-space: normal;
  word-break: break-word;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 38px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  z-index: 1000;
  margin: 0 auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  color: ${theme.colors.primary};
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: #fff;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 20px;
  position: relative;
  
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
  }
`;

const LessonCard = styled.div`
  background: ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return 'rgba(188, 231, 253, 0.13)';
      case 'ADULTS':
        return 'rgba(196, 146, 177, 0.08)';
      case 'SPECIAL':
        return 'rgba(219, 39, 98, 0.04)';
      default:
        return '#f5f5f5';
    }
  }};
  border: 1px solid ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return '#bce7fd';
      case 'ADULTS':
        return '#c492b1';
      case 'SPECIAL':
        return '#db2762';
      default:
        return '#e0e0e0';
    }
  }};
  border-radius: ${theme.borderRadius.medium};
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
`;

const LessonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const LessonTopic = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.1;
  flex: 1;
  min-width: 0;
`;

const LessonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: ${theme.colors.primary};
  min-width: 120px;
`;

const DetailValue = styled.span`
  color: ${theme.colors.primary};
  font-size: 1rem;
  text-align: right;
  font-weight: normal;
`;

const ActionSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const ActionTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0 0 16px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  
  
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 20px;
`;


const DangerButton = styled(ManageButton)`
  background: #dc3545;
  border: 2px solid #dc3545;
  min-width: 220px;
  color: white;
  box-shadow: none;
  &:hover {
    background: #c82333;
    border: 2px solid #c82333;
    color: white;
    box-shadow: none;
    transform: unset;
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const AcceptRescheduleButton = styled(AcceptButton)`
  border: 2px solid #4CAF50;
  background: #4CAF50;
  &:hover {
    border: 2px solid #45a049;
    background: #45a049;
    box-shadow: none;
    transform: unset;
  }

  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const EditButton = styled(ManageButton)`
  background: white;
  border: 2px solid ${theme.colors.accent1};
  min-width: 220px;
  color: black;
  box-shadow: none;
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
    box-shadow: none;
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const SaveButton = styled(EditButton)`
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  border: 2px solid ${theme.colors.secondary};
  color: white;
  box-shadow: none;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);

    border: 2px solid ${theme.colors.secondary};
    color: white;
    transform: unset;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: ${theme.colors.primary};
  font-size: 1.1rem;
`;

const SkeletonBox = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: ${theme.borderRadius.small};
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonHeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;

const SkeletonLessonBadge = styled(SkeletonBox)`
  width: 70px;
  height: 70px;
  border-radius: 35px;
  margin-right: 16px;
`;

const SkeletonMainTitle = styled(SkeletonBox)`
  height: 64px;
  width: 300px;
  @media (max-width: ${mobileBreakpoint}px) {
    height: 38px;
    width: 200px;
  }
`;

const SkeletonSubtitle = styled(SkeletonBox)`
  height: 32px;
  width: 400px;
  margin-bottom: 24px;
  @media (max-width: ${mobileBreakpoint}px) {
    height: 24px;
    width: 250px;
  }
`;

const SkeletonBackButton = styled(SkeletonBox)`
  height: 48px;
  width: 200px;
  margin-bottom: 20px;
`;

const SkeletonLessonCard = styled.div`
  background: rgba(188, 231, 253, 0.13);
  border: 1px solid #bce7fd;
  border-radius: ${theme.borderRadius.medium};
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const SkeletonLessonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const SkeletonLessonTopic = styled(SkeletonBox)`
  height: 28px;
  width: 60%;
  flex: 1;
`;

const SkeletonLessonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const SkeletonDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const SkeletonDetailLabel = styled(SkeletonBox)`
  height: 16px;
  width: 80px;
`;

const SkeletonDetailValue = styled(SkeletonBox)`
  height: 16px;
  width: 120px;
`;

const SkeletonActionSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const SkeletonActionTitle = styled(SkeletonBox)`
  height: 24px;
  width: 200px;
  margin-bottom: 16px;
`;

const SkeletonButton = styled(SkeletonBox)`
  height: 48px;
  width: 180px;
  margin-right: 12px;
  margin-bottom: 12px;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
  border-radius: ${theme.borderRadius.medium};
  padding: 16px;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
  border-radius: ${theme.borderRadius.medium};
  padding: 16px;
  margin-bottom: 20px;
`;

const WarningMessage = styled.div`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
  border-radius: ${theme.borderRadius.medium};
  padding: 16px;
  margin-bottom: 20px;
`;

const LessonSummary = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: ${theme.borderRadius.small};
  padding: 12px;
  color: ${theme.colors.primary};
  margin-top: 5px;
`;

const ManageLessonPage = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isReschedulePopupOpen, setIsReschedulePopupOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [isRejectConfirmationOpen, setIsRejectConfirmationOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [lessonSummary, setLessonSummary] = useState('');
  const [isEditingLessonSummary, setIsEditingLessonSummary] = useState(false);
  const [userLessonSummaries, setUserLessonSummaries] = useState({});
  const [editingUserSummaries, setEditingUserSummaries] = useState({});
  const [userRejectionReasons, setUserRejectionReasons] = useState({});
  const [isUserRejectConfirmationOpen, setIsUserRejectConfirmationOpen] = useState({});
  const [userAcceptSavingStates, setUserAcceptSavingStates] = useState({});
  const [userRejectSavingStates, setUserRejectSavingStates] = useState({});

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to reload lesson data from backend
  const reloadLessonData = async () => {
    if (!isAuthenticated() || !token || !lessonId) return;

    try {
      const response = await fetch(config.getApiUrl(`/api/lessons/${lessonId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLesson(data.lesson);
        setAdditionalInfo(data.lesson.additionalInfo || '');
        setLessonSummary(data.lesson.lessonSummary || '');
        
        // Initialize user lesson summaries for group lessons
        if (data.lesson.isGroupLesson && data.lesson.lessonSummary && typeof data.lesson.lessonSummary === 'object') {
          setUserLessonSummaries(data.lesson.lessonSummary);
        }
      } else {
        console.error('Failed to reload lesson data');
      }
    } catch (err) {
      console.error('Error reloading lesson data:', err);
    }
  };

  useEffect(() => {
    const fetchLesson = async () => {
      if (!isAuthenticated() || !token || !lessonId) return;
    
      // Small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const response = await fetch(config.getApiUrl(`/api/lessons/${lessonId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setLesson(data.lesson);
          setAdditionalInfo(data.lesson.additionalInfo || '');
          setLessonSummary(data.lesson.lessonSummary || '');
          
          // Initialize user lesson summaries for group lessons
          if (data.lesson.isGroupLesson && data.lesson.lessonSummary && typeof data.lesson.lessonSummary === 'object') {
            setUserLessonSummaries(data.lesson.lessonSummary);
          }
        } else {
          setError('Fehler beim Laden der Stunde');
        }
      } catch (err) {
        setError('Verbindungsfehler');
        console.error('Error fetching lesson:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [token, isAuthenticated, lessonId, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'UNCONFIRMED':
        return 'Ausstehend';
      case 'CONFIRMED':
        return 'Bestätigt';
      case 'CANCELLED':
        return 'Storniert';
      case 'DONE':
        return 'Abgeschlossen';
      case 'RESCHEDULE_REQUESTED':
        return 'Verschiebung angefragt';
      case 'SCHEDULING':
        return 'Geplant';
      default:
        return status;
    }
  };

  const isLessonExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  const shouldShowExpiryWarning = (lesson) => {
    if (!lesson) return false;
    const isExpired = isLessonExpired(lesson.expiryDate);
    const isActiveStatus = lesson.status === 'CONFIRMED' || lesson.status === 'UNCONFIRMED'  || lesson.status === 'RESCHEDULE_REQUESTED' || lesson.status === 'SCHEDULING';
    return isExpired && isActiveStatus;
  };

  const shouldShowExpiryWarning31Days = (lesson) => {
    if (!lesson || !lesson.expiryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(lesson.expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    const isActiveStatus = lesson.status === 'CONFIRMED' || lesson.status === 'UNCONFIRMED' || lesson.status === 'RESCHEDULE_REQUESTED' || lesson.status === 'SCHEDULING';
    return daysUntilExpiry <= 31 && daysUntilExpiry >= 0 && isActiveStatus;
  };

  const hasReachedRescheduleLimit = (lesson) => {
    const isConfirmedStatus = lesson.status === 'CONFIRMED' || lesson.status === 'SCHEDULING';
    return lesson && lesson.rescheduleCount >= 3 && isConfirmedStatus;
  };

  const isTrialLesson = (lesson) => {
    return lesson && lesson.category === 'TRIAL';
  };

  const calculateIsWithinXHours = (lesson) => {
    return isLessonWithinRestrictionHours(lesson, LESSON_CONFIG.CANCELLATION_RESTRICTION_HOURS);
  }

  const getCancellationMessage = (lesson) => {
    if (!lesson) {
      return 'Möchten du diese Stunde wirklich stornieren? Das Budget wird automatisch zurückerstattet.';
    }

    // For trial lessons when user is admin, show simple message
    if (isTrialLesson(lesson) && isUserAdmin()) {
      return 'Möchten du diese Stunde wirklich stornieren?';
    }

    const isExpired = isLessonExpired(lesson.expiryDate);
    const hasReachedLimit = hasReachedRescheduleLimit(lesson);
    const isRescheduleRequested = lesson.status === 'RESCHEDULE_REQUESTED';
    const isWithinXHours = calculateIsWithinXHours(lesson);
    
    // Base message
    let message = 'Möchten du diese Stunde wirklich stornieren?';
    
    // Check if budget will be refunded
    if ((hasReachedLimit || isExpired || isWithinXHours)) {
      if (isExpired) {
        message += ' Da das zugrundeliegende Stundenpaket abgelaufen ist, wird das Budget nicht zurückerstattet.';
        } else if (isWithinXHours) {
          message += ` Da diese Stunde in weniger als ${LESSON_CONFIG.CANCELLATION_RESTRICTION_HOURS} Stunden beginnt, wird das Budget nicht zurückerstattet.`;
      } else {
        message += ' Da diese Stunde bereits 3 Mal verschoben wurde, wird das Budget nicht zurückerstattet.';
      }
    } else {
      message += ' Das Budget wird automatisch zurückerstattet.';
    }
    
    return message;
  };

  // Function to determine which warning message to show with priority order
  const getWarningMessage = (lesson) => {
    // Admin users don't need warning messages
    if (isUserAdmin()) return null;
    
    if (!lesson) return null;

    // Priority 1: ExpiryWarningMessage (highest priority)
    if (shouldShowExpiryWarning(lesson)) {
      if (lesson.wasRescheduledByAdmin === true) {
        return `Das zugrundeliegend Stundenpaket ist am ${formatDate(lesson.expiryDate)} abgelaufen. Stornierst du die Stunde, wird das Budget nicht zurückerstattet.`;
      } else {
        return `Das zugrundeliegend Stundenpaket ist am ${formatDate(lesson.expiryDate)} abgelaufen. Diese Stunde kann nicht mehr verschoben werden. Stornierst du die Stunde, wird das Budget nicht zurückerstattet.`;
      }
    }

    // Priority 2: RescheduleLimitWarningMessage
    if (hasReachedRescheduleLimit(lesson)) {
      const inheritedText = lesson.inheritedRescheduleCount > 0 
        ? ` (${lesson.inheritedRescheduleCount} Verschiebung${lesson.inheritedRescheduleCount > 1 ? 'en' : ''} wurde${lesson.inheritedRescheduleCount > 1 ? 'n' : ''} von vorherigen Stunden übernommen)`
        : '';
      
      return `Diese${lesson.inheritedRescheduleCount > 0 ? ' oder eine vorherige ' : ' '}Stunde wurde bereits 3 Mal verschoben und kann nicht mehr umgeplant werden${inheritedText}.`;
    }

    // Priority 3: WithinXHoursWarningMessage
    if (calculateIsWithinXHours(lesson)) {
      return `Diese Stunde beginnt in weniger als ${LESSON_CONFIG.CANCELLATION_RESTRICTION_HOURS} Stunden. Sie kann nicht verschoben werden. Falls du die Stunde stornierst, wird das Budget nicht zurückerstattet.`;
    }

    // Priority 4: ExpiryWarning31DaysMessage (lowest priority)
    if (shouldShowExpiryWarning31Days(lesson)) {
      return `Das zugrundeliegende Stundenpaket läuft am ${formatDate(lesson.expiryDate)} ab. Falls du die Stunde stornierst, ist es gegenenfalls nicht mehr möglich, eine neuen Termin zu finden.`;
    }

    return null;
  };

  const handleSaveAdditionalInfo = async () => {
    if (!lesson) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(config.getApiUrl(`/api/lessons/${lessonId}/additional-info`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ additionalInfo })
      });

      if (response.ok) {
        setLesson(prev => ({ ...prev, additionalInfo }));
        setIsEditing(false);
      } else {
        setError('Fehler beim Speichern der Informationen');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Speichern');
      console.error('Error saving additional info:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLessonSummary = async () => {
    if (!lesson) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(config.getApiUrl(`/api/meyspage/lessons/${lessonId}/lesson-summary`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lessonSummary })
      });

      if (response.ok) {
        setLesson(prev => ({ ...prev, lessonSummary }));
        setIsEditingLessonSummary(false);
      } else {
        setError('Fehler beim Speichern der Stundenzusammenfassung');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Speichern der Stundenzusammenfassung');
      console.error('Error saving lesson summary:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUserLessonSummary = async (username) => {
    if (!lesson || !lesson.lessonBookingInfo || !lesson.lessonBookingInfo[username]) return;
    
    setSaving(true);
    setError(null);
    
    const userLessonId = lesson.lessonBookingInfo[username];
    const userSummary = userLessonSummaries[username] || '';
    
    try {
      const response = await fetch(config.getApiUrl(`/api/meyspage/lessons/${userLessonId}/lesson-summary`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lessonSummary: userSummary })
      });

      if (response.ok) {
        // Update the lesson data with the new summary
        setLesson(prev => ({
          ...prev,
          lessonSummary: {
            ...prev.lessonSummary,
            [username]: userSummary
          }
        }));
        
        // Exit edit mode for this user
        setEditingUserSummaries(prev => ({
          ...prev,
          [username]: false
        }));
      } else {
        setError('Fehler beim Speichern der Stundenzusammenfassung');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Speichern der Stundenzusammenfassung');
      console.error('Error saving user lesson summary:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelLesson = () => {
    if (!lesson) return;
    setCancellationReason('');
    setIsCancelConfirmationOpen(true);
  };

  const handleConfirmCancelLesson = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Determine the appropriate endpoint based on lesson type and user role
      let endpoint;
      if (isTrialLesson(lesson) && isUserAdmin()) {
        // Admin cancelling trial lesson - use MeysPage endpoint
        endpoint = `/api/meyspage/lessons/${lessonId}/cancel`;
      } else {
        // Regular user cancelling lesson or admin cancelling regular lesson - use regular endpoint
        endpoint = `/api/lessons/${lessonId}/cancel`;
      }

      const response = await fetch(config.getApiUrl(endpoint), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          cancellationReason: cancellationReason.trim() || null 
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLesson(prev => ({ 
          ...prev, 
          status: 'CANCELLED',
          cancellationReason: cancellationReason.trim() || null
        }));
        setIsCancelConfirmationOpen(false);
        setCancellationReason('');
      } else {
        setError('Fehler beim Stornieren der Stunde');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Stornieren');
      console.error('Error cancelling lesson:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRescheduleLesson = () => {
    setIsReschedulePopupOpen(true);
  };

  const handleLessonRescheduled = async (newLessonData) => {
    try {
      setIsReschedulePopupOpen(false);
      // Refresh the lesson data
      const updatedResponse = await fetch(config.getApiUrl(`/api/lessons/${lessonId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (updatedResponse.ok) {
        const data = await updatedResponse.json();
        setLesson(data.lesson);
      }
    } catch (err) {
      setError('Verbindungsfehler beim Aktualisieren der Stunde');
      console.error('Error refreshing lesson data:', err);
    }
  };

  const handleAcceptRescheduleRequest = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(config.getApiUrl(`/api/lessons/${lessonId}/accept-reschedule`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLesson(prev => ({ 
          ...prev, 
          status: data.lessonState || 'CONFIRMED'
        }));
      } else {
        setError('Fehler beim Akzeptieren der Verschiebung');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Akzeptieren der Verschiebung');
      console.error('Error accepting reschedule request:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptLesson = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(config.getApiUrl(`/api/meyspage/lessons/${lessonId}/accept`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLesson(prev => ({ 
          ...prev, 
          status: data.lessonState || 'CONFIRMED'
        }));
      } else {
        setError('Fehler beim Akzeptieren der Stunde');
        setSuccessMessage(null);
      }
    } catch (err) {
      setError('Verbindungsfehler beim Akzeptieren der Stunde');
      console.error('Error accepting lesson:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRejectLesson = () => {
    if (!lesson) return;
    setRejectionReason('');
    setIsRejectConfirmationOpen(true);
  };

  const handleConfirmRejectLesson = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(config.getApiUrl(`/api/meyspage/lessons/${lessonId}/reject`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          rejectionReason: rejectionReason.trim() || null 
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLesson(prev => ({ 
          ...prev, 
          status: 'CANCELLED'
        }));
        setIsRejectConfirmationOpen(false);
        setRejectionReason('');
      } else {
        setError('Fehler beim Ablehnen der Stunde');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Ablehnen der Stunde');
      console.error('Error rejecting lesson:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptUserLesson = async (username) => {
    if (!lesson || !lesson.lessonBookingInfo || !lesson.lessonBookingInfo[username]) return;
    
    setUserAcceptSavingStates(prev => ({ ...prev, [username]: true }));
    setError(null);
    
    const userLessonId = lesson.lessonBookingInfo[username];
    
    try {
      const response = await fetch(config.getApiUrl(`/api/meyspage/lessons/${userLessonId}/accept`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update the dependentStatus for this user and the main lesson status
        setLesson(prev => ({
          ...prev,
          status: data.lessonState || prev.status,
          dependentStatus: {
            ...prev.dependentStatus,
            [username]: data.lessonState || 'CONFIRMED'
          }
        }));
      } else {
        setError('Fehler beim Akzeptieren der Stunde');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Akzeptieren der Stunde');
      console.error('Error accepting user lesson:', err);
    } finally {
      setUserAcceptSavingStates(prev => ({ ...prev, [username]: false }));
    }
  };

  const handleRejectUserLesson = (username) => {
    if (!lesson || !lesson.lessonBookingInfo || !lesson.lessonBookingInfo[username]) return;
    setUserRejectionReasons(prev => ({
      ...prev,
      [username]: ''
    }));
    setIsUserRejectConfirmationOpen(prev => ({
      ...prev,
      [username]: true
    }));
  };

  const handleConfirmRejectUserLesson = async (username) => {
    if (!lesson || !lesson.lessonBookingInfo || !lesson.lessonBookingInfo[username]) return;
    
    setUserRejectSavingStates(prev => ({ ...prev, [username]: true }));
    setError(null);
    
    const userLessonId = lesson.lessonBookingInfo[username];
    const userRejectionReason = userRejectionReasons[username] || '';
    
    try {
      const response = await fetch(config.getApiUrl(`/api/meyspage/lessons/${userLessonId}/reject`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          rejectionReason: userRejectionReason.trim() || null 
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Update the dependentStatus for this user and potentially the main lesson status
        setLesson(prev => ({
          ...prev,
          status: data.lessonState || prev.status,
          dependentStatus: {
            ...prev.dependentStatus,
            [username]: 'CANCELLED'
          }
        }));
        setIsUserRejectConfirmationOpen(prev => ({
          ...prev,
          [username]: false
        }));
        setUserRejectionReasons(prev => ({
          ...prev,
          [username]: ''
        }));
        
        // Reload lesson data to reflect any changes in lesson structure
        await reloadLessonData();
      } else {
        setError('Fehler beim Ablehnen der Stunde');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Ablehnen der Stunde');
      console.error('Error rejecting user lesson:', err);
    } finally {
      setUserRejectSavingStates(prev => ({ ...prev, [username]: false }));
    }
  };

  // Function to calculate dynamic minimum width for group lesson buttons
  const calculateButtonMinWidth = () => {
    if (!lesson || !lesson.isGroupLesson || !lesson.dependentStatus) {
      return '200px'; // Default fallback
    }

    const unconfirmedUsers = Object.entries(lesson.dependentStatus)
      .filter(([username, status]) => status === 'UNCONFIRMED');

    if (unconfirmedUsers.length === 0) {
      return '200px'; // Default fallback
    }

    let longestNameLength = 0;

    unconfirmedUsers.forEach(([username, status]) => {
      const userIndex = lesson.users && Array.isArray(lesson.users) 
        ? lesson.users.findIndex((name, index) => 
            lesson.users && Array.isArray(lesson.users) && lesson.users[index] === username
          )
        : -1;
      
      const userDisplayName = userIndex >= 0 && lesson.userFirstName && Array.isArray(lesson.userFirstName) 
        ? lesson.userFirstName[userIndex] 
        : username;

      // Calculate the length of the button text: "{name} akzeptieren" and "{name} ablehnen"
      const acceptTextLength = `${userDisplayName} akzeptieren`.length;
      const rejectTextLength = `${userDisplayName} ablehnen`.length;
      
      longestNameLength = Math.max(longestNameLength, acceptTextLength, rejectTextLength);
    });

    // Base calculation: approximately 8-10px per character for button text
    // Add some padding for button styling (padding, borders, etc.)
    const calculatedWidth = Math.max(200, (longestNameLength * 9) + 40);
    
    return `${calculatedWidth}px`;
  };

  const SkeletonLoader = () => (
    <PageLayout>
      <HeroSection>
        <SkeletonHeroTitle>
          <SkeletonLessonBadge />
          <SkeletonMainTitle />
        </SkeletonHeroTitle>
        <SkeletonSubtitle />
      </HeroSection>
      
      <ContentContainer>
        <SkeletonBackButton />
        
        <SkeletonLessonCard>
          <SkeletonLessonHeader>
            <SkeletonLessonTopic />
          </SkeletonLessonHeader>
          
          <SkeletonLessonDetails>
            <SkeletonDetailItem>
              <SkeletonDetailLabel />
              <SkeletonDetailValue />
            </SkeletonDetailItem>
            <SkeletonDetailItem>
              <SkeletonDetailLabel />
              <SkeletonDetailValue />
            </SkeletonDetailItem>
            <SkeletonDetailItem>
              <SkeletonDetailLabel />
              <SkeletonDetailValue />
            </SkeletonDetailItem>
          </SkeletonLessonDetails>

          <SkeletonActionSection>
            <SkeletonActionTitle />
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <SkeletonButton />
              <SkeletonButton />
            </div>
          </SkeletonActionSection>
        </SkeletonLessonCard>
      </ContentContainer>
    </PageLayout>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!lesson) {
    return (
      <PageLayout>
        <ContentContainer>
          <ErrorMessage>Stunde nicht gefunden</ErrorMessage>
          <BackButton onClick={() => navigate(getBackNavigationPath())}>
            ← Zurück zu {getBackButtonText()}
          </BackButton>
        </ContentContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <LessonBadge>
            <svg width="38" height="38" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.5-0.24,1.03-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
            </svg>
          </LessonBadge>
          <MainTitle>Stunde verwalten</MainTitle>
        </HeroTitle>
        <Subtitle>Verwalte deine gebuchte Stunde</Subtitle>
      </HeroSection>
      
      <ContentContainer>
        <BackButton onClick={() => navigate(getBackNavigationPath())}>
          ← Zurück zu {getBackButtonText()}
        </BackButton>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        {getWarningMessage(lesson) && (
          <WarningMessage>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div>⚠️</div>
              <div style={{ flex: 1 }}>
                {getWarningMessage(lesson)}
              </div>
            </div>
          </WarningMessage>
        )}

        <LessonCard category={lesson.category}>
          <LessonHeader>
            <LessonTopic>
              {isUserAdmin() && lesson.userFirstName && !lesson.isGroupLesson ? (
                <>
                  {lesson.topic || 'Stunde'} mit {lesson.userFirstName}
                </>
              ) : (
                lesson.topic || 'Stunde'
              )}
            </LessonTopic>
          </LessonHeader>
          
          <LessonDetails>
            <DetailItem>
              <DetailLabel>Datum</DetailLabel>
              <DetailValue>{formatDate(lesson.date)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Zeit</DetailLabel>
              <DetailValue>{lesson.time}</DetailValue>
            </DetailItem>
            {isUserAdmin() && (
              <>
                <DetailItem>
                  <DetailLabel>Schüler</DetailLabel>
                  <DetailValue>
                    {lesson.isGroupLesson && Array.isArray(lesson.userName) ? (
                      lesson.userName.map((name, index) => (
                        <div key={index}>{name}</div>
                      ))
                    ) : (
                      lesson.userName || 'Nicht verfügbar'
                    )}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue style={{ wordBreak: 'break-all' }}>
                    {lesson.isGroupLesson && Array.isArray(lesson.email) ? (
                      lesson.email.map((email, index) => (
                        <div key={index}>{email}</div>
                      ))
                    ) : (
                      lesson.email || 'Nicht verfügbar'
                    )}
                  </DetailValue>
                </DetailItem>
              </>
            )}
            <DetailItem>
              <DetailLabel>Status</DetailLabel>
              <LessonStatus status={getStatusText(lesson.status)}>{getStatusText(lesson.status)}</LessonStatus>
            </DetailItem>
          </LessonDetails>

          {lesson.isGroupLesson && lesson.additionalInfo && typeof lesson.additionalInfo === 'object' ? (
            // Group lesson: Display consolidated section with all users' additionalInfo
            <ActionSection>
              <FormGroup>
                <ActionTitle>Zusätzliche Informationen</ActionTitle>
                <LessonSummary>
                  <InformationText style={{ fontSize: '1rem', minHeight: '65px' }}>
                    {Object.entries(lesson.additionalInfo).map(([username, userAdditionalInfo], index) => {
                      const userDisplayName = lesson.userFirstName && Array.isArray(lesson.userFirstName) && lesson.userFirstName[index] 
                        ? lesson.userFirstName[index] 
                        : username;
                      
                      const info = userAdditionalInfo || 'Keine zusätzlichen Informationen angegeben.';
                      
                      return (
                        <div key={username} style={{ marginBottom: index < Object.entries(lesson.additionalInfo).length - 1 ? '16px' : '0' }}>
                          <strong>{userDisplayName}: </strong>
                          {info}
                        </div>
                      );
                    })}
                  </InformationText>
                </LessonSummary>
              </FormGroup>
            </ActionSection>
          ) : (
            // Non-group lesson: Display single additionalInfo section
            <ActionSection>
              <FormGroup>
                <ActionTitle htmlFor="additionalInfo">Zusätzliche Informationen</ActionTitle>
                {isEditing ? (
                  <>
                    <TextArea
                      id="additionalInfo"
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      placeholder="Falls du bestimmte Themen in dieser Stunde besprechen möchtest, kannst du sie hier eintragen."
                    />
                    <ButtonGroup>
                      <EditButton 
                        onClick={() => {
                          setIsEditing(false);
                          setAdditionalInfo(lesson.additionalInfo || '');
                        }}
                        disabled={saving}
                      >
                        Abbrechen
                      </EditButton>
                      <SaveButton 
                        onClick={handleSaveAdditionalInfo}
                        disabled={saving}
                      >
                        {saving ? 'Speichere...' : 'Speichern'}
                      </SaveButton>
                    </ButtonGroup>
                  </>
                ) : (
                  <>
                    <LessonSummary>
                    <InformationText style={{ fontSize: '14px', minHeight: '65px' }}>
                      {lesson.additionalInfo || (isUserAdmin() ? 'Der Schüler hat keine zusätzlichen Informationen angegeben.' : 'Falls du bestimmte Themen in dieser Stunde besprechen möchtest, kannst du sie hier eintragen.')}
                    </InformationText>
                    </LessonSummary>
                    {(lesson.status !== 'CANCELLED' && lesson.status !== 'DONE') && !isUserAdmin() && (
                      <EditButton style={{ marginTop: '25px' }} onClick={() => setIsEditing(true)}>
                        Bearbeiten
                      </EditButton>
                    )}
                  </>
                )}
              </FormGroup>
            </ActionSection>
          )}

          {lesson.isGroupLesson && lesson.lessonSummary && typeof lesson.lessonSummary === 'object' ? (
            // Group lesson: Display individual sections for each user's lesson summary
            Object.entries(lesson.lessonSummary).map(([username, userLessonSummary], index) => {
              const userDisplayName = lesson.userFirstName && Array.isArray(lesson.userFirstName) && lesson.userFirstName[index] 
                ? lesson.userFirstName[index] 
                : username;
              
              const isEditingThisUser = editingUserSummaries[username] || false;
              const currentUserSummary = userLessonSummaries[username] || userLessonSummary || '';
              
              return (
                <ActionSection key={`summary-${username}`}>
                  <FormGroup>
                    <ActionTitle htmlFor={`lessonSummary-${username}`}>Zusammenfassung der Stunde - {userDisplayName}</ActionTitle>
                    {isEditingThisUser ? (
                      <>
                        <TextArea
                          id={`lessonSummary-${username}`}
                          value={currentUserSummary}
                          onChange={(e) => setUserLessonSummaries(prev => ({
                            ...prev,
                            [username]: e.target.value
                          }))}
                          placeholder="Zusammenfassung der Stunde (optional)"
                        />
                        <ButtonGroup>
                          <EditButton 
                            onClick={() => {
                              setEditingUserSummaries(prev => ({
                                ...prev,
                                [username]: false
                              }));
                              setUserLessonSummaries(prev => ({
                                ...prev,
                                [username]: userLessonSummary || ''
                              }));
                            }}
                            disabled={saving}
                          >
                            Abbrechen
                          </EditButton>
                          <SaveButton 
                            onClick={() => handleSaveUserLessonSummary(username)}
                            disabled={saving}
                          >
                            {saving ? 'Sende Email...' : 'Email senden'}
                          </SaveButton>
                        </ButtonGroup>
                      </>
                    ) : (
                      <>
                        <LessonSummary>
                          <InformationText style={{ fontSize: '14px', minHeight: '65px' }}>
                            {userLessonSummary || 'Noch nicht verfügbar.'}
                          </InformationText>
                        </LessonSummary>
                        {isUserAdmin() && lesson.status === 'DONE' && (
                          <EditButton 
                            style={{ marginTop: '25px' }} 
                            onClick={() => {
                              setEditingUserSummaries(prev => ({
                                ...prev,
                                [username]: true
                              }));
                              setUserLessonSummaries(prev => ({
                                ...prev,
                                [username]: userLessonSummary || ''
                              }));
                            }}
                          >
                            Bearbeiten
                          </EditButton>
                        )}
                      </>
                    )}
                  </FormGroup>
                </ActionSection>
              );
            })
          ) : (
            // Non-group lesson: Display single lesson summary section
            <ActionSection>
              <FormGroup>
                <ActionTitle htmlFor="lessonSummary">Zusammenfassung der Stunde</ActionTitle>
                {isEditingLessonSummary ? (
                  <>
                    <TextArea
                      id="lessonSummary"
                      value={lessonSummary}
                      onChange={(e) => setLessonSummary(e.target.value)}
                      placeholder="Zusammenfassung der Stunde (optional)"
                    />
                    <ButtonGroup>
                      <EditButton 
                        onClick={() => {
                          setIsEditingLessonSummary(false);
                          setLessonSummary(lesson.lessonSummary || '');
                        }}
                        disabled={saving}
                      >
                        Abbrechen
                      </EditButton>
                      <SaveButton 
                        onClick={handleSaveLessonSummary}
                        disabled={saving}
                      >
                        {saving ? 'Sende Email...' : 'Email senden'}
                      </SaveButton>
                    </ButtonGroup>
                  </>
                ) : (
                  <>
                    <LessonSummary>
                    <InformationText style={{ fontSize: '14px', minHeight: '65px' }}>
                      {lesson.lessonSummary || 'Noch nicht verfügbar.'}
                    </InformationText>
                    </LessonSummary>
                    {isUserAdmin() && lesson.status === 'DONE' && (
                      <EditButton style={{ marginTop: '25px' }} onClick={() => setIsEditingLessonSummary(true)}>
                        Bearbeiten
                      </EditButton>
                    )}
                  </>
                )}
              </FormGroup>
            </ActionSection>
          )}

          {(lesson.status !== 'CANCELLED' && lesson.status !== 'DONE') && (
            <ActionSection>
              <ActionTitle>Stunde verwalten</ActionTitle>
                            <ButtonGroup>
                {lesson.status === 'RESCHEDULE_REQUESTED' && (!isUserAdmin()) &&(
                  <AcceptRescheduleButton style={{ minWidth: 'unset' }}
                    onClick={handleAcceptRescheduleRequest}
                    disabled={saving}
                  >
                    {saving ? 'Akzeptiere...' : 'Verschiebung akzeptieren'}
                  </AcceptRescheduleButton>
                )}
                
                  {isUserAdmin() && lesson.status === 'UNCONFIRMED' && !lesson.isGroupLesson && (
                    <AcceptRescheduleButton style={{ minWidth: 'unset' }} onClick={handleAcceptLesson} disabled={saving}>
                      {saving ? 'Akzeptiere...' : 'Stunde akzeptieren'}
                    </AcceptRescheduleButton>
                  )}
                
                {(isUserAdmin() || (!hasReachedRescheduleLimit(lesson) && (!isLessonExpired(lesson.expiryDate) || lesson.wasRescheduledByAdmin === true) && !calculateIsWithinXHours(lesson))) && !isTrialLesson(lesson) && (
                  <EditButton style={{ minWidth: 'unset' }} onClick={handleRescheduleLesson}>
                    Stunde verschieben
                  </EditButton>
                )}
                {isUserAdmin() && lesson.status === 'UNCONFIRMED' && !lesson.isGroupLesson && (
                  <DangerButton style={{ minWidth: '220px' }} onClick={handleRejectLesson}>
                    Stunde ablehnen
                  </DangerButton>
                )}
                {((isTrialLesson(lesson) && lesson.status != 'UNCONFIRMED') || !isUserAdmin()) && (
                  <DangerButton style={{ minWidth: 'unset' }} onClick={handleCancelLesson}>
                    Stunde stornieren
                  </DangerButton>
                )}
              </ButtonGroup>
            </ActionSection>
          )}

          {/* Individual user management sections for group lessons */}
          {lesson.isGroupLesson && lesson.dependentStatus && isUserAdmin() && (
            Object.entries(lesson.dependentStatus)
              .filter(([username, status]) => status === 'UNCONFIRMED')
              .map(([username, status]) => {
                const userIndex = lesson.users && Array.isArray(lesson.users) 
                  ? lesson.users.findIndex((name, index) => 
                      lesson.users && Array.isArray(lesson.users) && lesson.users[index] === username
                    )
                  : -1;
                
                const userDisplayName = userIndex >= 0 && lesson.userFirstName && Array.isArray(lesson.userFirstName) 
                  ? lesson.userFirstName[userIndex] 
                  : username;
                
                return (
                  <ActionSection key={`user-management-${username}`}>
                    <ActionTitle>Stunde verwalten - {userDisplayName}</ActionTitle>
                    <ButtonGroup>
                      <AcceptRescheduleButton 
                        style={{ minWidth: calculateButtonMinWidth() }} 
                        onClick={() => handleAcceptUserLesson(username)} 
                        disabled={userAcceptSavingStates[username] || false}
                      >
                        {userAcceptSavingStates[username] ? 'Akzeptiere...' : `${userDisplayName} akzeptieren`}
                      </AcceptRescheduleButton>
                      <DangerButton 
                        style={{ minWidth: calculateButtonMinWidth() }} 
                        onClick={() => handleRejectUserLesson(username)}
                        disabled={userRejectSavingStates[username] || false}
                      >
                        {userRejectSavingStates[username] ? 'Ablehne...' : `${userDisplayName} ablehnen`}
                      </DangerButton>
                    </ButtonGroup>
                  </ActionSection>
                );
              })
          )}
        </LessonCard>
      </ContentContainer>

      <RescheduleLessonPopup
        isOpen={isReschedulePopupOpen}
        onClose={() => setIsReschedulePopupOpen(false)}
        onLessonRescheduled={handleLessonRescheduled}
        lessonId={lessonId}
        currentLesson={lesson}
        title="Stunde verschieben"
      />

      <ConfirmationPopup
        isOpen={isCancelConfirmationOpen}
        onClose={() => setIsCancelConfirmationOpen(false)}
        onConfirm={handleConfirmCancelLesson}
        title="Stunde stornieren"
        message={getCancellationMessage(lesson)}
        confirmText="Stunde stornieren"
        cancelText="Abbrechen"
        isDestructive={true}
        isLoading={saving}
        showTextArea={true}
        prefilledText={cancellationReason}
        placeholder="Grund für die Stornierung (optional)"
        onTextChange={setCancellationReason}
      />

      <ConfirmationPopup
        isOpen={isRejectConfirmationOpen}
        onClose={() => setIsRejectConfirmationOpen(false)}
        onConfirm={handleConfirmRejectLesson}
        title="Stunde ablehnen"
        message="Möchtest du diese Stunde wirklich ablehnen?"
        confirmText="Stunde ablehnen"
        cancelText="Abbrechen"
        isDestructive={true}
        isLoading={saving}
        showTextArea={true}
        prefilledText={rejectionReason}
        placeholder="Grund für die Ablehnung (optional)"
        onTextChange={setRejectionReason}
      />

      {/* Individual user rejection confirmation popups */}
      {lesson.isGroupLesson && lesson.dependentStatus && isUserAdmin() && (
        Object.entries(lesson.dependentStatus)
          .filter(([username, status]) => status === 'UNCONFIRMED')
          .map(([username, status]) => {
            const userIndex = lesson.users && Array.isArray(lesson.users) 
              ? lesson.users.findIndex((name, index) => 
                  lesson.users && Array.isArray(lesson.users) && lesson.users[index] === username
                )
              : -1;
            
            const userDisplayName = userIndex >= 0 && lesson.userFirstName && Array.isArray(lesson.userFirstName) 
              ? lesson.userFirstName[userIndex] 
              : username;

            return (
              <ConfirmationPopup
                key={`reject-confirmation-${username}`}
                isOpen={isUserRejectConfirmationOpen[username] || false}
                onClose={() => setIsUserRejectConfirmationOpen(prev => ({
                  ...prev,
                  [username]: false
                }))}
                onConfirm={() => handleConfirmRejectUserLesson(username)}
                title={`${userDisplayName} ablehnen`}
                message={`Möchtest du die Stunde für ${userDisplayName} wirklich ablehnen?`}
                confirmText={`Nutzer ablehnen`}
                cancelText="Abbrechen"
                isDestructive={true}
                isLoading={userRejectSavingStates[username] || false}
                showTextArea={true}
                prefilledText={userRejectionReasons[username] || ''}
                placeholder="Grund für die Ablehnung (optional)"
                onTextChange={(text) => setUserRejectionReasons(prev => ({
                  ...prev,
                  [username]: text
                }))}
              />
            );
          })
      )}
    </PageLayout>
  );
};

export default ManageLessonPage; 